import { useComposedRefs } from '@radix-ui/react-compose-refs';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import clsx from 'clsx';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { CloseIcon } from '@/components/icons/close';

import { Button } from './button';
import { Typography } from './typography';

const DialogContainerContext = React.createContext<HTMLElement | null>(null);

function useDialogContainer() {
  return React.useContext(DialogContainerContext);
}

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      data-slot="dialog-overlay"
      className={clsx(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 flex items-center justify-center bg-black/50',
        className,
      )}
      {...props}
    />
  );
});
DialogOverlay.displayName = 'DialogOverlay';

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  const [container, setContainer] = React.useState<HTMLElement | null>(null);
  const composedRefs = useComposedRefs(ref, setContainer);

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay>
        <DialogContainerContext.Provider value={container}>
          <DialogPrimitive.Content
            ref={composedRefs}
            data-slot="dialog-content"
            className={clsx(
              `data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 border-fx-neutral-400 relative z-50 flex max-h-[90vh] min-h-0 w-full max-w-[calc(100%-2rem)] flex-col rounded-lg border bg-white shadow-lg duration-200 sm:max-w-lg`,
              className,
            )}
            {...props}
          >
            {children}
            <DialogPrimitive.Close
              asChild
              className="absolute top-2 right-2 z-20"
            >
              <Button
                variant="secondary"
                size="icon"
                className="h-8 w-8 rounded-md"
              >
                <CloseIcon />
                <span className="sr-only">Close</span>
              </Button>
            </DialogPrimitive.Close>
          </DialogPrimitive.Content>
        </DialogContainerContext.Provider>
      </DialogOverlay>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={clsx(
        'bg-gradient-fx-neutral-50 border-fx-neutral-200 flex h-12 shrink-0 items-center justify-center rounded-t-lg border-b p-2',
        className,
      )}
      {...props}
    />
  );
}

function DialogBody({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-body"
      className={clsx(
        'min-h-0 flex-1 overflow-y-auto overscroll-contain',
        className,
      )}
      {...props}
    />
  );
}

function DialogFooter({
  className,
  children,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={clsx(
        'border-fx-neutral-200 flex shrink-0 items-center justify-between rounded-b-lg border-t bg-white p-2',
        className,
      )}
      {...props}
    >
      <DialogPrimitive.Close asChild>
        <Button variant="secondary">
          <FormattedMessage
            defaultMessage="Cancel"
            description="Default dismiss on standard modal footer"
          />
        </Button>
      </DialogPrimitive.Close>
      {children}
    </div>
  );
}

function DialogTitle({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={className}
      {...props}
    >
      <Typography variant="body2">{children}</Typography>
    </DialogPrimitive.Title>
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={className}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContainerContext,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  useDialogContainer,
};
